import React, { useEffect } from 'react'
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Box,
  Typography,
  withStyles,
} from '@material-ui/core'
import { ProviderContent, StepZero, StepOne, StepTwo } from '..'
import styles from '../../styles'
import { useDispatch, useSelector } from 'react-redux'
import { CoreButton } from 'src/components/core'
import CheckIcon from '@material-ui/icons/Check'
import StepThree from './step-three'

function getSteps() {
  return [
    'Create Payment Link',
    'Add Success URL',
    'Add Webhook',
    'Set Display Price',
  ]
}

function getStepContent(
  collectionId,
  priceData,
  index,
  content,
  handleNext,
  handleBack,
  entity,
  paymentLink,
  setPaymentLink,
  paymentSettingsConfigured,
  paymentProvider,
  markStepCompleted,
  onClickDone,
  settings,
  onPatchConfig,
  onPatchPrice,
  paymentSettingType,
) {
  switch (index) {
    case 0:
      return (
        <StepZero
          collectionId={collectionId}
          entity={entity}
          videoUrl={content[index]}
          handleNext={handleNext}
          paymentLink={paymentLink}
          setPaymentLink={setPaymentLink}
          settings={settings}
          onPatchConfig={onPatchConfig}
        />
      )
    case 1:
      return (
        <StepOne
          collectionId={collectionId}
          videoUrl={content[index]}
          handleNext={handleNext}
          handleBack={handleBack}
          entity={entity}
        />
      )
    case 2:
      return (
        <StepTwo
          collectionId={collectionId}
          handleBack={handleBack}
          videoUrl={content[index]}
          entity={entity}
          handleNext={handleNext}
          paymentSettingsConfigured={paymentSettingsConfigured}
          paymentProvider={paymentProvider}
        />
      )
    case 3:
      return (
        <StepThree
          collectionId={collectionId}
          priceData={priceData}
          handleBack={handleBack}
          videoUrl={content[index]}
          entity={entity}
          handleNext={handleNext}
          paymentSettingsConfigured={paymentSettingsConfigured}
          paymentProvider={paymentProvider}
          markStepCompleted={markStepCompleted}
          onClickDone={onClickDone}
          settings={settings}
          onPatchPrice={onPatchPrice}
          paymentSettingType={paymentSettingType}
        />
      )
    default:
      return 'Unknown step'
  }
}

function StepperContent(props) {
  const {
    priceData,
    entity,
    paymentProvider,
    onClickDone,
    collectionId,
    classes,
    providerChangeCount,
    settings: settingsProp,
    onPatchConfig,
    onPatchPrice,
    paymentSettingType,
  } = props
  const dispatch = useDispatch()
  const content = ProviderContent(paymentProvider)
  const steps = getSteps()

  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const settings = settingsProp ?? collectionSettings
  const [
    paymentSettingsConfigured,
    setPaymentSettingsConfigured,
  ] = React.useState(settings?.isPaymentConfigured ?? false)

  const [activeStep, setActiveStep] = React.useState(
    settings?.isPaymentConfigured ? steps.length - 1 : 0,
  )
  const [paymentLink, setPaymentLink] = React.useState(settings?.paymentLink)

  const [completedSteps, setCompletedSteps] = React.useState(
    paymentSettingsConfigured ? [0, 1, 2, 3] : [],
  )

  // The settings prop usually arrives async from the GET; resync local mirror
  // state when isPaymentConfigured/paymentLink change so the wizard doesn't
  // remain stuck at step 0 after a prior save loads in.
  useEffect(() => {
    setPaymentSettingsConfigured(settings?.isPaymentConfigured ?? false)
  }, [settings?.isPaymentConfigured])

  useEffect(() => {
    setPaymentLink(settings?.paymentLink)
  }, [settings?.paymentLink])

  useEffect(() => {
    if (paymentSettingsConfigured) {
      setActiveStep(steps.length - 1)
      setCompletedSteps([0, 1, 2, 3])
    } else {
      setActiveStep(0)
      setCompletedSteps([])
    }
  }, [paymentSettingsConfigured])

  const handleNext = () => {
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep])
    }

    if (activeStep + 1 === steps.length) {
      setActiveStep(steps.length)
      onClickDone()
    } else {
      setActiveStep(prevActiveStep => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleStep = step => () => {
    if (isStepCompleted(step) || step === 0) {
      setActiveStep(step)
    }
  }

  const isStepCompleted = step => {
    return completedSteps.includes(step)
  }

  const markStepCompleted = () => {
    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep])
    }
  }

  // Custom StepIcon component
  const CustomStepIcon = props => {
    const { active, completed, icon } = props

    return (
      <div
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: completed
            ? '#29D25D'
            : active
            ? '#FFFFFF'
            : '#d9d9d9',
          fontSize: '14px',
          fontWeight: 600,
          color: completed ? '#FFFFFF' : active ? '#212B36' : '#637381',
        }}
      >
        {completed ? (
          <CheckIcon style={{ fontSize: '16px', color: '#FFFFFF' }} />
        ) : (
          icon
        )}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Stepper
        activeStep={activeStep}
        sx={{
          padding: 0,
          '& .MuiStep-root': {
            padding: 0,
          },
          '& .MuiStepButton-root': {
            margin: 0,
            padding: '8px',
            width: '180px',
          },
          '& .MuiStepButton-root.Mui-disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
          },
          '& .MuiStepLabel-label': {
            fontSize: '14px',
            fontWeight: 500,
            color: '#637381',
            marginLeft: '8px',
          },
          '& .MuiStepLabel-label.Mui-active': {
            color: '#212B36',
            fontWeight: 600,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: '#29D25D',
            fontWeight: 500,
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: '#29D25D',
          },
        }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={isStepCompleted(index)}>
            <StepButton
              onClick={handleStep(index)}
              disabled={
                !isStepCompleted(index) &&
                index !== 0 &&
                !isStepCompleted(index - 1)
              }
            >
              <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ padding: '8px' }}>
        {getStepContent(
          collectionId,
          priceData,
          activeStep,
          content,
          handleNext,
          handleBack,
          entity,
          paymentLink,
          setPaymentLink,
          paymentSettingsConfigured,
          paymentProvider,
          markStepCompleted,
          onClickDone,
          settings,
          onPatchConfig,
          onPatchPrice,
          paymentSettingType,
        )}
      </Box>
    </div>
  )
}

export default withStyles(styles)(StepperContent)
